// deps
import React, { memo, useCallback, useContext, ReactElement, useMemo, useState, useEffect } from "react";

// styles
import cx from "classnames";

// internal deps
import { TickScreen, TICK_ACCENT, TICK_STATES, UpiIntentPoller } from "@swiggy-private/payment-ui";
import { BOOLEAN_AS_STRING, OrderData } from "@swiggy-private/payment-types";
import { CheckoutContext, CheckoutActionTypes, AnalyticsContext, isToingFlow } from "@swiggy-private/checkout-core";
import PaymentTxnStatusPollerWrapper from "./PaymentTxnStatusPollerWrapper";
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";

//interfaces
import { RouteProps } from "@payment-client/interfaces/Router";

//hooks
import usePollingTxnStatus from "@payment-client/hooks/UsePollingTxnStatus";
import useNativeCustomBackHandling from "./hooks/useNativeCustomBackHandling";

//constants
import { TEXT_TYPES } from "@payment-client/constants/ConfirmScreen";
import { EXPERIMENTS_VALUES } from "@payment-client/constants/Experiments";

//helpers
import { isSuperUserOrder } from "@payment-client/helpers/OrderHelper";
import { calculateItemCountForOrder } from "./helpers";
import { getPollingConfiguration } from "@payment-client/helpers/PollingHelpers";
import {
    getConfirmOrderTexts,
    getNewExperimentValue,
    getPaymentCodeFromOrderData,
    getSuccessInfoData,
    isPrebookPrepaid,
} from "@payment-client/helpers/PaymentHelper";
import { getBusinessLine, isBusinessLinePayOnDelivery, isBusinessLineSwiggyMoney } from "@payment-client/helpers/Misc";
import {
    upiIntentBackClickEvent,
    upiIntentCancelHalfCardCancelClickEvent,
    upiIntentImpressionEvent,
} from "@payment-client/analytics/events/UpiIntent";
import { getSubTextNodeList } from "@payment-client/helpers/OrderConfirmHelper";
import styles from "../confirm-order/OrderConfirmSubText.scss";

interface PaymentTxnStatusPollerContentProps {
    orderData: OrderData;
}

export function PaymentTxnStatusPoller(_props: RouteProps): ReactElement | null {
    const { checkoutState } = useContext(CheckoutContext);

    if (checkoutState.type !== CheckoutActionTypes.PAYMENT_TXN_STATUS_POLLER) {
        return null;
    }

    return <PaymentTxnStatusPollerContent orderData={checkoutState.payload.order} />;
}

export const PaymentTxnStatusPollerContent = memo(function PaymentTxnStatusPollerContent(
    props: PaymentTxnStatusPollerContentProps,
): React.ReactElement {
    const [showCancelModal, setShowCancelModal] = useState(false);
    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);
    const { paymentResponse } = useContext(ShowPaymentContext);
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const paymentCode = getPaymentCodeFromOrderData(checkoutState.payload?.placeOrderData);
    const { pollingIntervalInMs, pollingDurationInMs } = getPollingConfiguration(paymentCode || "", props.orderData);

    const onBack = useCallback((): void => {
        upiIntentBackClickEvent(clickEvent);
        setShowCancelModal(true);
    }, [clickEvent]);

    useEffect(() => {
        upiIntentImpressionEvent(impressionEvent);
    }, [impressionEvent]);

    const removeCustomBack = useNativeCustomBackHandling(onBack);

    const txnStatusStop = usePollingTxnStatus({
        pollingDurationInMs,
        pollingIntervalInMs,
        startTime:
            checkoutState.type === CheckoutActionTypes.PAYMENT_TXN_STATUS_POLLER
                ? checkoutState?.payload?.startTime
                : undefined,
        paymentCode: paymentCode || "",
        removeCustomBackHandling: removeCustomBack,
    });

    // To Get the tick state based on business line
    const businessLine = getBusinessLine();
    const isToing = useMemo(() => isToingFlow(), []);

    const isPrebookPrepaidCart = useMemo(() => isPrebookPrepaid(props.orderData), [props.orderData]);
    const successDataInfo = useMemo(() => getSuccessInfoData(props.orderData), [props.orderData]);
    const successData = useMemo(
        () =>
            getConfirmOrderTexts({
                businessLine,
                tickState: TICK_STATES.LOADING,
                isPostOrderTip: false,
                isToing,
                successDataInfo,
                isPrebookPrepaidCart,
            }),
        [businessLine, isToing, successDataInfo],
    );
    const mainText = successData[TEXT_TYPES.MAIN];
    const subText = successData[TEXT_TYPES.SUB];

    const subTextNodeClassName = cx({
        [styles.stealDealSuccessInfo]: isPrebookPrepaidCart,
    });

    const [itemCount, orderTotal] = useMemo(() => {
        if (checkoutState.type !== CheckoutActionTypes.PAYMENT_TXN_STATUS_POLLER) {
            return [0, 0];
        }

        try {
            const {
                order: { orderItems = [], orderTotal, childOrders = [] },
            } = checkoutState.payload;

            const itemCount = calculateItemCountForOrder(orderItems, childOrders);
            return [itemCount, orderTotal];
        } catch (error) {
            return [0, 0];
        }
    }, [checkoutState.payload, checkoutState.type]);

    const showUpiCancel = useMemo(
        () =>
            (getNewExperimentValue(
                paymentResponse,
                EXPERIMENTS_VALUES.ENABLE_UPI_INTENT_CANCEL,
            ) as BOOLEAN_AS_STRING) === BOOLEAN_AS_STRING.TRUE,
        [paymentResponse],
    );

    const isSuper = useCallback((): boolean => {
        return isSuperUserOrder(props.orderData);
    }, [props.orderData]);

    const getSuperMessage = useCallback(() => {
        if (!isSuper() || props.orderData == null) {
            return;
        }
        let successMessage = "";
        let successMessageInfo = "";

        if (props.orderData.successMessage) {
            successMessage = props.orderData.successMessage;
            successMessageInfo = props.orderData.successMessageInfo ?? "";
        }
        return {
            successMessage,
            successMessageInfo,
        };
    }, [props.orderData, isSuper]);

    const subTextNodeList = useMemo(
        () =>
            getSubTextNodeList({
                state: TICK_STATES.LOADING,
                subText,
                getSuperMessageCb: getSuperMessage,
                className: subTextNodeClassName,
            }),
        [subText],
    );

    const getAccent = useCallback(() => {
        if (isBusinessLineSwiggyMoney() || isBusinessLinePayOnDelivery()) {
            return TICK_ACCENT.GREEN;
        }
        if (isPrebookPrepaidCart) {
            return TICK_ACCENT.LIGHT_GREEN;
        }
    }, [isPrebookPrepaidCart]);

    const onClickCancel = useCallback(
        (show: boolean): void => {
            setShowCancelModal(show);
        },
        [setShowCancelModal],
    );

    const onCancelTransaction = useCallback((): void => {
        if (checkoutState.type === CheckoutActionTypes.PAYMENT_TXN_STATUS_POLLER) {
            txnStatusStop();
            upiIntentCancelHalfCardCancelClickEvent(clickEvent);

            setCheckoutState({
                type: CheckoutActionTypes.CONFIRM_ORDER,
                payload: {
                    orderId: String(checkoutState.payload.order.orderId),
                    order: checkoutState.payload.order,
                    placeOrderData: checkoutState.payload.placeOrderData,
                },
            });
        }
    }, [
        checkoutState.payload.order,
        checkoutState.payload.placeOrderData,
        checkoutState.type,
        clickEvent,
        setCheckoutState,
        txnStatusStop,
    ]);

    if (showUpiCancel) {
        return (
            <PaymentTxnStatusPollerWrapper onBack={onBack}>
                <UpiIntentPoller
                    tickState={TICK_STATES.LOADING}
                    mainText={mainText}
                    subText={subText}
                    isSuper={isSuper()}
                    isSwiggyMoney={isBusinessLineSwiggyMoney()}
                    accent={getAccent()}
                    showCancelModal={showCancelModal}
                    setShowCancelModal={onClickCancel}
                    onCancelTransaction={onCancelTransaction}
                    paymentAmount={String(orderTotal)}
                    itemCount={itemCount}
                    isStealStoreCart={isPrebookPrepaidCart}
                    subTextNodeList={subTextNodeList}
                />
            </PaymentTxnStatusPollerWrapper>
        );
    }

    return (
        <TickScreen
            tickState={TICK_STATES.LOADING}
            mainText={mainText}
            subText={subText}
            isSuper={isSuper()}
            isSwiggyMoney={isBusinessLineSwiggyMoney()}
            accent={getAccent()}
            isStealStoreCart={isPrebookPrepaidCart}
            subTextNodeList={subTextNodeList}
        />
    );
});
